/* Getting off a build that no longer exists.

   A service worker answers navigations from its own precache before the
   network is consulted. That is the whole point of it, and it is also the one
   way this app can wedge itself: if the precached `index.html` names chunks
   the server has since replaced, every route import 404s, and no amount of
   reloading helps — the reload is served by the same worker, from the same
   manifest, naming the same missing files. The user sees an app that will not
   start and has no way to tell it to stop trying.

   The fix is the one a developer would apply by hand: drop the worker, drop
   the caches, reload onto the network. It runs at most once per tab, because
   a recovery that can retrigger itself is a reload loop, and a reload loop is
   worse than the blank screen it was trying to fix.

   Only derived data is touched. The basket, the session and the saved
   providers live in localStorage and are deliberately left alone — this is
   recovery, not a factory reset. */

/** Marks a tab as having already tried. sessionStorage, not local: a new tab
 *  on a genuinely broken deploy should still get its one attempt. */
const ATTEMPTED = 'hexclean:v1:recovered'

/** Vite, Safari and Firefox each phrase a failed dynamic import differently,
 *  and the only thing they have in common is the word "module". */
const CHUNK_ERROR =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|dynamically imported module|loading chunk|timed out loading a route chunk/i

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  return CHUNK_ERROR.test(message)
}

/** True if this tab has already spent its one recovery attempt. */
export function hasAttemptedRecovery(): boolean {
  try {
    return sessionStorage.getItem(ATTEMPTED) === '1'
  } catch {
    /* Storage disabled. Treating that as "already attempted" is the safe way
       round: without somewhere to record the attempt there is nothing to stop
       it happening again on the next load, and that is the reload loop. */
    return true
  }
}

/** Clears the worker and its caches, then reloads.
 *
 *  Returns `false` without doing anything if this tab has already tried, which
 *  is the caller's signal to show the manual recovery UI instead. */
export async function recoverFromStaleBuild(): Promise<boolean> {
  if (hasAttemptedRecovery()) return false

  try {
    sessionStorage.setItem(ATTEMPTED, '1')
  } catch {
    return false
  }

  // Both cleanups are best-effort and independent: a browser that refuses one
  // should still get the benefit of the other, and neither is worth failing
  // the reload over.
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((r) => r.unregister()))
    } catch {
      /* no worker, or a browser that will not say */
    }
  }

  if (typeof caches !== 'undefined') {
    try {
      const names = await caches.keys()
      await Promise.all(names.map((name) => caches.delete(name)))
    } catch {
      /* private mode, or storage pressure */
    }
  }

  /* `location.replace` rather than `reload`: a reload can be answered from the
     back/forward cache with the very state we are trying to leave. */
  window.location.replace(window.location.href)
  return true
}
